<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import SingleSelect from './SingleSelect.vue';

// Valor do operador "No mês": 'AAAA-MM', ou '' enquanto falta o mês ou o ano de 4 dígitos.
const month = defineModel({ type: String, required: true });

const { t, locale } = useI18n();

const MONTH_VALUE = /^(\d{4})-(\d{2})$/;
const YEAR_VALUE = /^\d{4}$/;
const MONTHS_IN_YEAR = 12;

const monthOptions = computed(() => {
  const format = new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    month: 'long',
    timeZone: 'UTC',
  });
  return Array.from({ length: MONTHS_IN_YEAR }, (_, index) => ({
    id: index + 1,
    name: format.format(new Date(Date.UTC(2000, index, 1))),
  }));
});

const selectedMonth = ref({});
const year = ref(new Date().getFullYear());
// Last value this field emitted: the model coming back with it is our own echo, anything else (a saved
// folder, or the row clearing the value when the attribute changes) is applied to the fields.
let emittedValue = null;

watch(
  month,
  value => {
    if (value === emittedValue) return;
    const match = MONTH_VALUE.exec(value ?? '');
    if (!match) {
      selectedMonth.value = {};
      return;
    }
    year.value = Number(match[1]);
    selectedMonth.value = monthOptions.value[Number(match[2]) - 1] ?? {};
  },
  { immediate: true }
);

watch([selectedMonth, year], ([monthOption, yearValue]) => {
  const hasValue = monthOption?.id && YEAR_VALUE.test(String(yearValue));
  emittedValue = hasValue
    ? `${yearValue}-${String(monthOption.id).padStart(2, '0')}`
    : '';
  month.value = emittedValue;
});
</script>

<template>
  <div class="flex gap-2">
    <SingleSelect
      v-model="selectedMonth"
      disable-search
      :options="monthOptions"
      :placeholder="t('FILTER.MONTH_INPUT.MONTH_PLACEHOLDER')"
      dropdown-max-height="max-h-64"
    />
    <Input
      v-model="year"
      type="number"
      class="w-24 [&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0"
      :placeholder="t('FILTER.MONTH_INPUT.YEAR_PLACEHOLDER')"
    />
  </div>
</template>
